const PERSIAN_DIGITS = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']

export const toPersianDigits = (value: string | number): string => {
    return String(value).replace(/[0-9]/g, (digit) => PERSIAN_DIGITS[Number(digit)])
}

export const formatNumber = (value: number, fractionDigits = 0): string => {
    const safe = Number.isFinite(value) ? value : 0
    const [integer, fraction] = Math.abs(safe).toFixed(fractionDigits).split('.')
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, '٬')
    const sign = safe < 0 ? '−' : ''

    return toPersianDigits(`${sign}${grouped}${fraction ? `٫${fraction}` : ''}`)
}

const formatTrimmed = (value: number): string => {
    const rounded = Math.round(value * 10) / 10
    return formatNumber(rounded, Number.isInteger(rounded) ? 0 : 1)
}

export const formatCompact = (value: number): string => {
    if (value >= 1_000_000) return `${formatTrimmed(value / 1_000_000)} میلیون`
    if (value >= 1_000) return `${formatTrimmed(value / 1_000)} هزار`

    return formatNumber(value)
}

export const formatRating = (value: number): string => formatNumber(value, 1)

export const formatHours = (minutes: number): string => {
    return `${formatTrimmed(minutes / 60)} ساعت`
}

export const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60)
    const rest = Math.round(minutes % 60)

    if (!hours) return `${formatNumber(rest)} دقیقه`
    if (!rest) return `${formatNumber(hours)} ساعت`

    return `${formatNumber(hours)} ساعت و ${formatNumber(rest)} دقیقه`
}

export const formatPersianDate = (value: string | null | undefined): string | null => {
    if (!value) return null

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) return null

    return new Intl.DateTimeFormat('fa-IR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'UTC'
    }).format(date)
}

export const initialsOf = (name: string): string => {
    const cleaned = name.replace(/^(دکتر|مهندس|استاد)\s+/u, '').trim()
    const parts = cleaned.split(/\s+/).filter(Boolean)

    if (!parts.length) return '؟'
    if (parts.length === 1) return parts[0].slice(0, 1)

    return `${parts[0].slice(0, 1)}‌${parts[parts.length - 1].slice(0, 1)}`
}

export const mixHex = (hex: string, target: string, amount: number): string => {
    const parse = (value: string): Array<number> | null => {
        const match = /^#?([0-9a-f]{6})$/i.exec(value.trim())
        if (!match) return null
        const int = parseInt(match[1], 16)
        return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
    }

    const from = parse(hex)
    const to = parse(target)
    if (!from || !to) return hex

    const mixed = from.map((channel, index) => Math.round(channel + (to[index] - channel) * amount))
    return `#${mixed.map((channel) => channel.toString(16).padStart(2, '0')).join('')}`
}

export const safeColor = (value: string | null | undefined, fallback = '#39367A'): string => {
    return value && /^#[0-9a-f]{6}$/i.test(value.trim()) ? value.trim() : fallback
}
